// Copyright 2026, University of Maryland, All Rights Reserved
/**
 * The Expedited Privacy Review track: eligibility determination and vendor terms review in
 * one panel.
 *
 * The two halves are one review, not two stacked results — the terms review only happens
 * because the determination cleared it. Reading both datasets in a single renderer is what
 * lets the terms section explain itself: a request routed to full SRM has no terms findings
 * *and never will*, which a standalone findings renderer can only report as "nothing here".
 *
 * The determination lives on the intake form and the findings on the vendor agreement, so
 * both arrive as separate entries in `documentSlotData`; each half reads the documents that
 * carry its own slot.
 */
import { type FC } from 'react'
import { ScrollText } from 'lucide-react'
import type { RendererComponentProps } from '@/app-shell/renderer-components'
import { SrmEligibilityRenderer, readVerdict } from './SrmEligibilityRenderer'
import { SrmFindingsRenderer } from './SrmFindingsRenderer'
import { ELIGIBLE_VERDICT } from '../utils/eligibility'

/** Slot carrying the vendor terms findings. Matches SrmFindingsRenderer's slot key. */
const TERMS_SLOT = 'findings'

/** Why the terms section is empty, in the request's own terms. */
const TermsPlaceholder: FC<{ verdict: string | null }> = ({ verdict }) => {
  const message =
    verdict === null
      ? 'Run review to generate findings.'
      : verdict.toUpperCase() === ELIGIBLE_VERDICT
        ? 'No terms findings yet — upload the vendor agreement and run review.'
        : `Not reviewed — the terms review runs only for ${ELIGIBLE_VERDICT} requests.`
  return <div className="px-4 py-3 text-sm text-muted-foreground">{message}</div>
}

export const SrmExpeditedReviewRenderer: FC<RendererComponentProps> = (props) => {
  const { documentSlotData = [] } = props
  const termsDocs = documentSlotData.filter((d) => d.slotData[TERMS_SLOT])

  return (
    <div>
      <SrmEligibilityRenderer {...props} />

      <div className="border-t">
        <div className="flex items-center gap-2 px-4 pt-3 text-xs font-medium text-muted-foreground uppercase tracking-wider">
          <ScrollText className="h-3.5 w-3.5" />
          Vendor Terms
        </div>
        {termsDocs.length > 0 ? (
          <SrmFindingsRenderer {...props} documentSlotData={termsDocs} />
        ) : (
          <TermsPlaceholder verdict={readVerdict(documentSlotData)} />
        )}
      </div>
    </div>
  )
}
